import type { JSX, ReactNode } from "react";

import { Badge } from "@/components/ui/badge";
import { STATUS_DOT, statusVariant } from "@/lib/status-colors";
import { cn } from "@/lib/utils";

export interface StatusBadgeProps {
  /** Raw backend status: a run, session, sandbox-build or integration state. */
  readonly status: string;
  /** Overrides the humanized status text. */
  readonly label?: ReactNode;
  readonly className?: string;
}

/**
 * The dot alone, for places where the surrounding text already names the status
 * (a sidebar entry, a table row whose next cell is the status word).
 */
export function StatusDot({ status, className }: Omit<StatusBadgeProps, "label">): JSX.Element {
  return (
    <span
      aria-hidden="true"
      data-status={statusVariant(status)}
      className={cn(
        "inline-block size-[7px] shrink-0 rounded-full",
        STATUS_DOT[statusVariant(status)],
        className,
      )}
    />
  );
}

/**
 * Labelled status pill. Every status narrows to one of the seven `--status-*`
 * variants in `lib/status-colors.ts`, so an unknown status renders neutral grey
 * rather than borrowing a color that already means something else.
 */
export function StatusBadge({ status, label, className }: StatusBadgeProps): JSX.Element {
  const variant = statusVariant(status);
  return (
    <Badge className={cn("gap-1.5", className)} variant={variant}>
      <span
        aria-hidden="true"
        className={cn("size-1.5 shrink-0 rounded-full", STATUS_DOT[variant])}
      />
      {label ?? status.replaceAll("_", " ")}
    </Badge>
  );
}
